import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Zap, Globe, Brush, HeartHandshake, Blend, Shield, Mail, UserPlus } from 'lucide-react';
import MilestoneCard from '../components/about/MilestoneCard.jsx';
import SEO from '../components/SEO';

export default function About() {
  const { t } = useTranslation();

  const companyValues = [
    {
      icon: <Zap className="w-8 h-8 text-values-icon" />,
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.desc'),
    },
    {
      icon: <Globe className="w-8 h-8 text-values-icon" />,
      title: t('about.values.accessibility.title'),
      description: t('about.values.accessibility.desc'),
    },
    {
      icon: <Shield className="w-8 h-8 text-values-icon" />,
      title: t('about.values.empowerment.title'),
      description: t('about.values.empowerment.desc'),
    },
    {
      icon: <HeartHandshake className="w-8 h-8 text-values-icon" />,
      title: t('about.values.connection.title'),
      description: t('about.values.connection.desc'),
    },
    {
      icon: <Blend className="w-8 h-8 text-values-icon" />,
      title: t('about.values.transparency.title'),
      description: t('about.values.transparency.desc'),
    },
    {
      icon: <Brush className="w-8 h-8 text-values-icon" />,
      title: t('about.values.creativityFirst.title'),
      description: t('about.values.creativityFirst.desc'),
    },
  ];

  const milestones = [
    {
      year: '2025',
      title: t('about.milestones.m1.title'),
      description: t('about.milestones.m1.desc'),
    },
    {
      year: '2025',
      title: t('about.milestones.m2.title'),
      description: t('about.milestones.m2.desc'),
    },
    {
      year: '2025',
      title: t('about.milestones.m3.title'),
      description: t('about.milestones.m3.desc'),
    },
    {
      year: '2025',
      title: t('about.milestones.m4.title'),
      description: t('about.milestones.m4.desc'),
    },
  ];

  return (
    <>
      <SEO
        title="About Kreatives - Nordic Creative Talent Platform | Our Mission & Values"
        description="Learn about Kreatives' mission to connect Nordic creative talent with global opportunities. Discover our values, milestones, and commitment to empowering creatives across Sweden, Norway, Denmark, Finland, and Iceland."
        keywords="about kreatives, nordic creative platform, creative talent, mission, values, sweden, norway, denmark, finland, iceland, creative community"
        image="/logo-kreativ-light.png"
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About Kreatives",
          "description": "Learn about Kreatives' mission to connect Nordic creative talent with global opportunities",
          "mainEntity": {
            "@type": "Organization",
            "name": "Kreatives",
            "description": "Nordic Creative Talent Platform",
            "foundingDate": "2025",
            "areaServed": ["Sweden", "Norway", "Denmark", "Finland", "Iceland"]
          }
        }}
      />
      <div className="min-h-screen flex flex-col">
      <main className="flex-1 divide-y divide-zinc-200 dark:divide-zinc-800 space-y-8">
        {/* Hero Section */}
        <section className="py-6 px-4">
          <div className="container">
            <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-4">
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 text-primary-text">
                    {t('about.aboutUs')}
                  </h1>
                  <p className="text-xl max-w-2xl mx-auto opacity-90 text-secondary-text">
                    {t('about.aboutUsText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center p-8">
              <h2 className="text-3xl font-bold mb-6 text-primary-text">{t('about.ourMission')}</h2>
              <p className="text-xl text-secondary-text leading-relaxed">
                {t('about.ourMissionText')}
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="p-8">
              <div className="flex flex-col md:flex-row gap-12 items-center">
                <div className="md:w-1/2">
                  <img
                    src="/about/kreativ-team-collaboration.jpg"
                    alt="Kreativ team collaboration - Creative professionals working together in modern workspace"
                    className="rounded-lg shadow-md w-full h-auto"
                  />
                </div>
                <div className="md:w-1/2">
                  <h2 className="text-3xl font-bold mb-6 text-primary-text">
                    {t('about.ourStory')}
                  </h2>
                  <div className="space-y-4 text-secondary-text">
                    <p>
                      {t('about.ourStoryText')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center text-primary-text">
              {t('about.ourValues')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {companyValues.map((value, index) => (
                <div
                  key={index}
                  className="p-8 rounded-lg shadow-sm bg-card-bg values-card card-hover"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center values-icon">
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-primary-text">{value.title}</h3>
                  </div>
                  <p className="text-secondary-text">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones - Timeline layout */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold mb-12 text-center text-primary-text">
              {t('about.ourJourney')}
            </h2>
            <div className="p-8">
              <div className="relative">
                {/* Timeline vertical line */}
                <div className="absolute left-0 md:left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-timeline" />
                <div className="relative pt-8 pb-2">
                  {milestones.map((m, i) => (
                    <MilestoneCard key={i} milestone={m} index={i} totalCards={milestones.length} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Join Us CTA */}
        <section className="py-10 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center p-8">
              <h2 className="text-3xl font-bold mb-4 text-primary-text">{t('about.title')}</h2>
              <p className="text-xl text-secondary-text mb-8 max-w-2xl mx-auto">
                {t('about.text')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-3 whitespace-nowrap"
                  data-variant="primary"
                >
                  <Mail className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  {t('footer.contact')}
                </Link>
                <Link
                  to="/creatives"
                  className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-3 whitespace-nowrap"
                  data-variant="primary"
                >
                  <UserPlus className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                  Become a Creative
                </Link>
                </div>
              </div>
            </div>
        </section>
      </main>
      </div>
    </>
  );
}
